import React, { useState, useEffect, useCallback } from 'react';
import { authenticatedFetch } from '../api/apiClient';

// MUI Icons
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

interface InlineSlideViewerProps {
  exportId: string;
  iterationFqn: string; // Keep same prop name for compatibility - can be either iteration or article FQN
  fileCount: number;
}

export const InlineSlideViewer: React.FC<InlineSlideViewerProps> = ({ 
  exportId, 
  iterationFqn, 
  fileCount 
}) => {
  const normalizedFqn = iterationFqn?.toLowerCase() || '';
  const isArticle = normalizedFqn.startsWith('atlas_');
  const isLibraryItem = normalizedFqn.startsWith('spark_library_item@');
  const apiPath = isArticle ? 'atlas/articles' : (isLibraryItem ? 'spark/library/items' : 'spark/iterations');
  const [effectiveSlide, setEffectiveSlide] = useState(1); // What the user perceives as current
  const [slideA, setSlideA] = useState(1); // First iframe
  const [slideB, setSlideB] = useState(1); // Second iframe (always mounted)
  const [showA, setShowA] = useState(true); // Which iframe is currently visible
  const [slideFiles, setSlideFiles] = useState<string[]>([]);

  // Build slide list by probing only actual HTML slides (slide_N.html),
  // since exports may include non-HTML assets (e.g., PNGs).
  useEffect(() => {
    let cancelled = false;
    const detectSlides = async () => {
      const maxCandidates = Math.max(1, Math.min(fileCount || 1, 200)); // safety cap
      const found: string[] = [];
      for (let i = 1; i <= maxCandidates; i++) {
        const name = `slide_${i}.html`;
        const url = `/api/${apiPath}/${encodeURIComponent(iterationFqn)}/exports/${exportId}/files/${name}`;
        try {
          // Prefer HEAD; fall back to GET if HEAD unsupported
          let resp = await authenticatedFetch(url, { method: 'HEAD' });
          if (!resp.ok || resp.status === 405) {
            resp = await authenticatedFetch(url, { method: 'GET', headers: { 'Accept': 'text/html' } });
          }
          if (resp.ok) {
            found.push(name);
          } else {
            // Stop at first missing slide index (slides are sequential)
            break;
          }
        } catch (_) {
          break;
        }
      }
      // Ensure at least the first slide is present as a fallback
      const slides = found.length > 0 ? found : ['slide_1.html'];
      if (!cancelled) {
        setSlideFiles(slides);
        // Reset to first slide when files change
        setEffectiveSlide(1);
        setSlideA(1);
        setSlideB(1);
        setShowA(true);
      }
    };
    detectSlides();
    return () => { cancelled = true; };
  }, [fileCount, exportId, iterationFqn, apiPath]);

  const iframeTitleFor = (slideIndex: number) =>
    `Slide ${slideIndex} preview for ${iterationFqn}`;

  const handleSlideLoad = useCallback((loadedSlide: number) => {
    if (loadedSlide === effectiveSlide) {
      return;
    }
    setShowA((prev) => !prev);
    setEffectiveSlide(loadedSlide);
  }, [effectiveSlide]);

  const handlePrevSlide = useCallback(() => {
    if (effectiveSlide > 1) {
      const newSlide = effectiveSlide - 1;
      const hiddenSlide = showA ? slideB : slideA;

      if (hiddenSlide === newSlide) {
        // Already pre-loaded; swap immediately
        handleSlideLoad(newSlide);
      } else {
        // Trigger load in hidden iframe
        if (showA) {
          setSlideB(newSlide);
        } else {
          setSlideA(newSlide);
        }
      }
    }
  }, [effectiveSlide, handleSlideLoad, showA, slideA, slideB]);

  const handleNextSlide = useCallback(() => {
    if (effectiveSlide < slideFiles.length) {
      const newSlide = effectiveSlide + 1;
      const hiddenSlide = showA ? slideB : slideA;

      if (hiddenSlide === newSlide) {
        // Already pre-loaded; swap immediately
        handleSlideLoad(newSlide);
      } else {
        // Trigger load in hidden iframe
        if (showA) {
          setSlideB(newSlide);
        } else {
          setSlideA(newSlide);
        }
      }
    }
  }, [effectiveSlide, handleSlideLoad, showA, slideA, slideB, slideFiles.length]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        handlePrevSlide();
      } else if (e.key === 'ArrowRight') {
        handleNextSlide();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handlePrevSlide, handleNextSlide]);

  return (
    <div className="inline-slide-viewer">
      {/* Main slide area */}
      <div className="inline-slide-main">
        {/* Navigation - Previous (only show if not on first slide) */}
        {effectiveSlide > 1 && (
          <button
            className="inline-slide-nav inline-slide-nav-prev"
            onClick={handlePrevSlide}
            title="Previous slide"
          >
            <ChevronLeftIcon fontSize="large" />
          </button>
        )}

        {/* Slide container */}
        <div className="inline-slide-container">
          {/* Iframe A */}
          {slideFiles[slideA - 1] && (
            <iframe
              key="iframe-a"
              src={`/api/${apiPath}/${encodeURIComponent(iterationFqn)}/exports/${exportId}/files/${slideFiles[slideA - 1]}`}
              className="inline-slide-iframe"
              title={iframeTitleFor(slideA)}
              style={{ 
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                opacity: showA ? 1 : 0,
                zIndex: showA ? 2 : 1,
                transition: 'opacity 0.3s ease-in-out',
                transitionDelay: showA ? '0s' : '0.2s'
              }}
              onLoad={() => !showA && handleSlideLoad(slideA)}
              onError={() => !showA && handleSlideLoad(slideA)}
            />
          )}
          
          {/* Iframe B */}
          {slideFiles[slideB - 1] && (
            <iframe
              key="iframe-b"
              src={`/api/${apiPath}/${encodeURIComponent(iterationFqn)}/exports/${exportId}/files/${slideFiles[slideB - 1]}`}
              className="inline-slide-iframe"
              title={iframeTitleFor(slideB)}
              style={{ 
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                opacity: showA ? 0 : 1,
                zIndex: showA ? 1 : 2,
                transition: 'opacity 0.3s ease-in-out',
                transitionDelay: showA ? '0.2s' : '0s'
              }}
              onLoad={() => showA && handleSlideLoad(slideB)}
              onError={() => showA && handleSlideLoad(slideB)}
            />
          )}
        </div>

        {/* Navigation - Next (only show if not on last slide) */}
        {effectiveSlide < slideFiles.length && (
          <button
            className="inline-slide-nav inline-slide-nav-next"
            onClick={handleNextSlide}
            title="Next slide"
          >
            <ChevronRightIcon fontSize="large" />
          </button>
        )}
      </div>
    </div>
  );
}; 
